/**
 * IREKS — Kulmbach, Bavaria, malting since 1856.
 *
 * Colour is published in EBC (with a °L column alongside); `colorEbc` is the
 * mid-point of the published band and the °L figure is the conversion
 * `°L = (EBC + 1.2) / 2.65` rounded to one decimal. The two columns on the
 * product overview agree with that conversion, which is the check that it is
 * the one they used.
 *
 * Extract and moisture are published on the per-product spec sheets as a
 * minimum and a maximum respectively (`> 80 %`, `< 5 %`) for the base malts
 * we have read (Pilsner, Vienna). Those limits are stored as-is. IREKS
 * publishes no diastatic power anywhere in the range, so every enzymatic row
 * carries the class-typical °Lintner this package already holds for its class
 * and is `representative`. Crystal, roasted and acidulated rows take DP 0 by
 * physics; they are still `representative` because extract and moisture for
 * those SKUs are class-typical rather than read off a sheet.
 *
 * No row carries a `url`. IREKS publishes one overview page for the whole
 * line and the spec-sheet PDFs are not addressed per product on their own
 * domain, so there is no maker page for an individual SKU.
 *
 * ## The SPM tags, and the rows that are deliberately not here
 *
 * Stone Path Malt of Wareham, Massachusetts sells a "Franconian" line under
 * their own SPM label, and it is this malt: their category page says the
 * series is theirs exclusively through a partnership with IREKS, the product
 * photography is IREKS', and the published analyses match these rows figure
 * for figure — SPM Pilsner is 80 % extract, 5 % moisture and 2.5-5 EBC, which
 * is `ireks-pilsner` exactly. So the SPM names are `spm-*` tags on the malt
 * they name rather than five more rows: a reseller's label is a name for a
 * product, not another product, and duplicating a maltster under every
 * distributor that relabels them would give the importer's matcher five
 * identical candidates for the word "Pilsner" and a brewer five identical
 * rows to choose between.
 *
 * The one SPM product that was genuinely missing is Spitz malt, which Stone
 * Path sell as Signature Foam and which had no row here at all. Its figures
 * come from the analysis on that page, since IREKS' own overview does not
 * carry it.
 *
 * @see https://www.ireks-malz.de/en/malts/produktuebersicht
 * @see https://stonepathmalt.com/product-category/franconian-malts/
 */

import type { Fermentable } from '../../schema/fermentable';

/** IREKS malts. @see Fermentable */
export const ireksFermentables: readonly Fermentable[] = [
  {
    id: 'ireks-spitz',
    name: 'Spitz Malt',
    maker: 'IREKS',
    origin: 'Germany',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 70,
    moisturePercent: 5,
    colorLovibond: 1.8,
    colorEbc: 3.5,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Germination stopped early, so the rootlets still show and the malt keeps the high-molecular protein a fully modified grain would have spent. It is bought to firm up foam and to give a very well modified grist something to push against. Extract is low on purpose, and IREKS publish it as a ceiling rather than a floor, which is the opposite of every other malt in their range.',
    tags: [
      'base',
      'german',
      'foam',
      'head-retention',
      'low-modification',
      'spitz',
      'spm-signature-foam',
    ],
    provenance: 'representative',
  },
  {
    id: 'ireks-pilsner',
    name: 'Pilsner Malt',
    maker: 'IREKS',
    origin: 'Germany',
    type: 'base',
    maltClass: 'base-pilsner',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 1.9,
    colorEbc: 3.8,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale Kulmbach pilsner malt, well modified and enzyme-rich enough to carry a helles or pils on its own. A quiet, grain-sweet base that stays out of the way of hops.',
    tags: ['base', 'lager', 'german', 'pilsner', 'spm-pilsner'],
    provenance: 'representative',
  },
  {
    id: 'ireks-pale-ale',
    name: 'Pale Ale Malt',
    maker: 'IREKS',
    origin: 'Germany',
    type: 'base',
    maltClass: 'base-pale',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 2.7,
    colorEbc: 6,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Kilned a shade warmer than the pilsner malt, so the wort lands gold rather than straw. The continental choice when an ale grist wants more malt body than pilsner gives, without going as far as Vienna.',
    tags: ['base', 'ale', 'german', 'pale-ale', 'spm-pale'],
    provenance: 'representative',
  },
  {
    id: 'ireks-vienna',
    name: 'Vienna Malt',
    maker: 'IREKS',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 3.8,
    colorEbc: 9,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Amber-kilned malt that still converts itself, so it can be the whole grist. Brings a golden-orange cast and a toasted-bread sweetness aimed at export, Märzen and Vienna lager.',
    tags: ['base', 'kilned', 'german', 'vienna', 'spm-vienna'],
    provenance: 'representative',
  },
  {
    id: 'ireks-munich',
    name: 'Munich Malt',
    maker: 'IREKS',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 80,
    moisturePercent: 5,
    colorLovibond: 8,
    colorEbc: 20,
    diastaticPowerLintner: 50,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Dark-kilned Munich with a bread-crust aroma and enough enzyme to stand alone. Use it whole for dunkel and bock, or at 10–30 % to put a malt spine under a pale grist.',
    tags: ['base', 'kilned', 'german', 'munich', 'spm-munich-dark'],
    provenance: 'representative',
  },
  {
    id: 'ireks-mela-red-alder',
    name: 'Mela Red Alder',
    maker: 'IREKS',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 17.4,
    colorEbc: 45,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Stewed then kilned to force melanoidins, throwing a red-amber hue and a decoction-like malt depth. A few percent colors a pale beer red without crystal sweetness.',
    tags: ['kilned', 'german', 'melanoidin', 'red', 'decoction'],
    provenance: 'representative',
  },
  {
    id: 'ireks-mela-beech',
    name: 'Mela Beech',
    maker: 'IREKS',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 32.5,
    colorEbc: 85,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darker of the two Mela grades: stewed and kilned to a brown rather than red, with a stronger Maillard maltiness. Use it to deepen bock, dunkel and brown ale without roast.',
    tags: ['kilned', 'german', 'melanoidin', 'brown', 'decoction'],
    provenance: 'representative',
  },
  {
    id: 'ireks-aroma',
    name: 'Aroma Malt',
    maker: 'IREKS',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 78,
    moisturePercent: 4.5,
    colorLovibond: 106.1,
    colorEbc: 280,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest kiln malt in the range, not a roast: deep color and a full malt aroma without the ash of chocolate malt. A percent or two stands in for a decoction on a dark lager.',
    tags: ['kilned', 'german', 'melanoidin', 'dark', 'decoction'],
    provenance: 'representative',
  },
  {
    id: 'ireks-crystal-maple',
    name: 'Crystal Maple',
    maker: 'IREKS',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 78,
    moisturePercent: 6,
    colorLovibond: 2.2,
    colorEbc: 4.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The palest IREKS crystal, used for body and foam rather than color. Three to ten percent plump a helles or pils without shifting the shade off straw.',
    tags: ['crystal', 'caramel', 'german', 'dextrin', 'body', 'head-retention', 'light'],
    provenance: 'representative',
  },
  {
    id: 'ireks-crystal-birch',
    name: 'Crystal Birch',
    maker: 'IREKS',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 5.2,
    colorEbc: 12.5,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 20,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A very pale crystal that adds a little gold and a gentle honey sweetness. The light seasoning in a pale ale or helles that wants more body than Maple gives.',
    tags: ['crystal', 'caramel', 'german', 'light', 'body'],
    provenance: 'representative',
  },
  {
    id: 'ireks-crystal-oak',
    name: 'Crystal Oak',
    maker: 'IREKS',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 11.8,
    colorEbc: 30,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Light caramel malt with a hint of toffee and a pale-brown cast. Five to ten percent rounds a pale ale or a helles without turning it amber.',
    tags: ['crystal', 'caramel', 'german', 'light', 'toffee'],
    provenance: 'representative',
  },
  {
    id: 'ireks-crystal-mahogany',
    name: 'Crystal Mahogany',
    maker: 'IREKS',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 23.1,
    colorEbc: 60,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Medium crystal with caramel sweetness and a reddish hue. The usual 5–10 % dose for altbier, Märzen and red ale when the color is the point as much as the flavor.',
    tags: ['crystal', 'caramel', 'german', 'red'],
    provenance: 'representative',
  },
  {
    id: 'ireks-crystal-rosewood',
    name: 'Crystal Rosewood',
    maker: 'IREKS',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 75,
    moisturePercent: 6,
    colorLovibond: 45.7,
    colorEbc: 120,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Darker caramel with strong toffee and a hint of roast. A few percent colors and sweetens a bitter, a brown ale or a bock without going as far as ebony.',
    tags: ['crystal', 'caramel', 'german', 'toffee'],
    provenance: 'representative',
  },
  {
    id: 'ireks-crystal-teak',
    name: 'Crystal Teak',
    maker: 'IREKS',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 74,
    moisturePercent: 5.5,
    colorLovibond: 57.1,
    colorEbc: 150,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'A step darker than Rosewood, with more caramelized-sugar depth and a copper-brown cast. Keep it at 3–8 %; above that the sweetness starts to dominate.',
    tags: ['crystal', 'caramel', 'german', 'dark'],
    provenance: 'representative',
  },
  {
    id: 'ireks-crystal-ebony',
    name: 'Crystal Ebony',
    maker: 'IREKS',
    origin: 'Germany',
    type: 'crystal-caramel',
    maltClass: 'crystal',
    extractFineDryPercent: 72,
    moisturePercent: 5,
    colorLovibond: 151.4,
    colorEbc: 400,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 8,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'The darkest IREKS crystal: burnt sugar, liquorice and a lot of color. A percent or two is enough for porter, schwarzbier or a dark abbey ale.',
    tags: ['crystal', 'caramel', 'german', 'dark', 'burnt-sugar'],
    provenance: 'representative',
  },
  {
    id: 'ireks-chocolate',
    name: 'Chocolate Malt',
    maker: 'IREKS',
    origin: 'Germany',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 70,
    moisturePercent: 4.5,
    colorLovibond: 340.1,
    colorEbc: 900,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Roasted barley malt at chocolate color: cocoa and coffee without the acrid edge of black malt. Three to five percent is the usual dose in porter and dark lager.',
    tags: ['roasted', 'german', 'chocolate', 'coffee'],
    provenance: 'representative',
  },
  {
    id: 'ireks-black',
    name: 'Black Malt',
    maker: 'IREKS',
    origin: 'Germany',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 65,
    moisturePercent: 4.5,
    colorLovibond: 528.8,
    colorEbc: 1400,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Taken all the way to a black roast. Colors a beer jet-black and adds a dry roast bitterness; late mash addition keeps the harshness down. One to three percent is plenty.',
    tags: ['roasted', 'german', 'black'],
    provenance: 'representative',
  },
  {
    id: 'ireks-sour',
    name: 'Sour Malt',
    maker: 'IREKS',
    origin: 'Germany',
    type: 'base',
    maltClass: 'acidulated',
    extractFineDryPercent: 78,
    moisturePercent: 6,
    colorLovibond: 1.7,
    colorEbc: 3.3,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 10,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pilsner malt soured with lactic acid, up to 4.5 % by weight. The Reinheitsgebot-legal mash-pH lever: start around 1 % of the grist and titrate against the water.',
    tags: ['acidulated', 'german', 'ph', 'sauermalz'],
    provenance: 'representative',
  },
  {
    id: 'ireks-smoked',
    name: 'Smoked Malt',
    maker: 'IREKS',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'base-pale',
    extractFineDryPercent: 77,
    moisturePercent: 5.5,
    colorLovibond: 2.5,
    colorEbc: 5.5,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 100,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Beech-smoked malt for rauchbier: ham and wood-smoke over a pale base. It converts itself, so it can be the whole grist; most beers want 20–60 % unless the point is the smoke.',
    tags: ['smoked', 'german', 'rauchbier', 'beech'],
    provenance: 'representative',
  },
  {
    id: 'ireks-rye',
    name: 'Rye Malt',
    maker: 'IREKS',
    origin: 'Germany',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 81,
    moisturePercent: 5.5,
    colorLovibond: 3.1,
    colorEbc: 7,
    diastaticPowerLintner: 30,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Malted rye: spicy, slightly peppery and very high in arabinoxylan, so the mash turns sticky above about 20 %. Rice hulls are not optional at high rates, and the payoff is a creamy, bread-crust rye beer.',
    tags: ['base', 'rye', 'german', 'spicy'],
    provenance: 'representative',
  },
  {
    id: 'ireks-biscuit',
    name: 'Biscuit Malt',
    maker: 'IREKS',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'munich-vienna',
    extractFineDryPercent: 75,
    moisturePercent: 4.5,
    colorLovibond: 19.3,
    colorEbc: 50,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 15,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Toasted rather than roasted: biscuit, bread crust and a little nuttiness with no roast bitterness. Three to ten percent puts a baked-malt top note on Märzen, amber ale or brown ale.',
    tags: ['kilned', 'toasted', 'german', 'biscuit', 'bread'],
    provenance: 'representative',
  },
  {
    id: 'ireks-wheat-light',
    name: 'Wheat Malt Light',
    maker: 'IREKS',
    origin: 'Germany',
    type: 'base',
    maltClass: 'wheat',
    extractFineDryPercent: 83,
    moisturePercent: 5.5,
    colorLovibond: 2,
    colorEbc: 4,
    diastaticPowerLintner: 76,
    suggestedMaxPercent: 70,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Pale malted wheat, high in extract and protein. Builds the dense head and soft body of a weissbier, and at 5–15 % firms up foam in almost anything.',
    tags: ['base', 'wheat', 'german', 'head-retention', 'spm-wheat-malt-light'],
    provenance: 'representative',
  },
  {
    id: 'ireks-wheat-dark',
    name: 'Wheat Malt Dark',
    maker: 'IREKS',
    origin: 'Germany',
    type: 'kilned-toasted',
    maltClass: 'wheat',
    extractFineDryPercent: 82,
    moisturePercent: 5.5,
    colorLovibond: 6.5,
    colorEbc: 16,
    diastaticPowerLintner: 22,
    suggestedMaxPercent: 50,
    needsMashing: true,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Wheat malt kilned to an amber color, adding bread-crust depth alongside the usual wheat softness. The obvious lever for a dunkles weissbier.',
    tags: ['wheat', 'german', 'dark'],
    provenance: 'representative',
  },
  {
    id: 'ireks-wheat-black',
    name: 'Wheat Malt Black',
    maker: 'IREKS',
    origin: 'Germany',
    type: 'roasted',
    maltClass: 'roasted',
    extractFineDryPercent: 72,
    moisturePercent: 5,
    colorLovibond: 528.8,
    colorEbc: 1400,
    diastaticPowerLintner: 0,
    suggestedMaxPercent: 5,
    needsMashing: false,
    diPh: null,
    bufferingCapacity: null,
    description:
      'Roasted wheat, husk-free by nature and therefore smoother than black barley malt. Gives dark wheat beers and stouts a cocoa depth with less astringency than the barley equivalent.',
    tags: ['roasted', 'wheat', 'german', 'black'],
    provenance: 'representative',
  },
];
